import { useState } from "react";
import { NavLink, Outlet, useNavigate, Navigate } from "react-router-dom";
import { LayoutDashboard, Wrench, Package, ShoppingBag, Users, BarChart3, LogOut, ExternalLink, Menu, X, Smartphone, Settings, Sparkles, Tag, Mail, Send, Tablet } from "lucide-react";

const NAV = [
  { to: "/admin", label: "Übersicht", icon: LayoutDashboard, end: true },
  { to: "/admin/reparaturen", label: "Reparaturen", icon: Wrench },
  { to: "/admin/repair-catalog", label: "iPad & MacBook", icon: Tablet },
  { to: "/admin/ankauf", label: "Handy-Ankauf", icon: Sparkles },
  { to: "/admin/kontakt", label: "Kontakt", icon: Mail },
  { to: "/admin/modelle", label: "Modelle & Preise", icon: Smartphone },
  { to: "/admin/produkte", label: "Produkte", icon: Package },
  { to: "/admin/bestellungen", label: "Bestellungen", icon: ShoppingBag },
  { to: "/admin/kunden", label: "Kunden", icon: Users },
  { to: "/admin/rabatte", label: "Rabatte", icon: Tag },
  { to: "/admin/emails", label: "E-Mail-Queue", icon: Send },
  { to: "/admin/analytics", label: "Analytics", icon: BarChart3 },
  { to: "/admin/einstellungen", label: "Einstellungen", icon: Settings },
];

export default function AdminLayout() {
  const nav = useNavigate();
  const [open, setOpen] = useState(false);

  if (!localStorage.getItem("hdh_token")) return <Navigate to="/admin/login" replace />;

  const logout = () => {
    localStorage.removeItem("hdh_token");
    nav("/admin/login");
  };

  return (
    <div className="min-h-screen bg-[#0A0A0B]" data-testid="admin-layout">
      {/* Mobile topbar */}
      <div className="lg:hidden sticky top-0 z-30 flex items-center justify-between px-5 h-14 bg-[#0F0F11]/95 backdrop-blur border-b border-white/5">
        <img src="/logo.png" alt="Haus des Handys" className="h-7 w-auto" />
        <button
          onClick={() => setOpen(true)}
          className="p-2 -mr-2 text-white"
          data-testid="admin-menu-toggle"
          aria-label="Menü öffnen"
        >
          <Menu className="w-6 h-6" />
        </button>
      </div>

      {/* Mobile backdrop */}
      {open && (
        <div
          className="lg:hidden fixed inset-0 z-40 bg-black/60 backdrop-blur-sm"
          onClick={() => setOpen(false)}
          data-testid="admin-backdrop"
        />
      )}

      {/* Sidebar */}
      <aside
        className={`w-72 lg:w-64 bg-[#0F0F11] text-white flex flex-col fixed inset-y-0 left-0 border-r border-white/5 z-50 transition-transform duration-300 ease-out ${
          open ? "translate-x-0" : "-translate-x-full"
        } lg:translate-x-0`}
        data-testid="admin-sidebar"
      >
        <div className="px-6 py-6 border-b border-white/5 flex items-center justify-between">
          <div>
            <img src="/logo.png" alt="Haus des Handys" className="h-10 w-auto" />
            <div className="text-[10px] uppercase tracking-[0.22em] text-zinc-500 mt-3">Admin Panel · v2.0</div>
          </div>
          <button
            onClick={() => setOpen(false)}
            className="lg:hidden p-2 -mr-2 text-zinc-400 hover:text-white"
            aria-label="Menü schließen"
            data-testid="admin-menu-close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto">
          {NAV.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.end}
              onClick={() => setOpen(false)}
              data-testid={`admin-nav-${n.label.toLowerCase()}`}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-colors ${
                  isActive ? "bg-[#DC1928] text-white" : "text-zinc-400 hover:text-white hover:bg-white/5"
                }`
              }
            >
              <n.icon className="w-4 h-4" />
              {n.label}
            </NavLink>
          ))}
        </nav>

        <div className="px-3 py-4 border-t border-white/5 space-y-1">
          <a href="/" target="_blank" rel="noreferrer" className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm text-zinc-400 hover:text-white hover:bg-white/5" data-testid="admin-open-site">
            <ExternalLink className="w-4 h-4" /> Website öffnen
          </a>
          <button onClick={logout} className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm text-zinc-400 hover:text-white hover:bg-white/5" data-testid="admin-logout">
            <LogOut className="w-4 h-4" /> Abmelden
          </button>
        </div>
      </aside>

      <main className="lg:ml-64">
        <div className="px-5 sm:px-6 lg:px-10 py-6 sm:py-8 lg:py-10">
          <Outlet />
        </div>
      </main>
    </div>
  );
}
